// src/pages/Institutional/AboutUs.js
import React from 'react'
import {
  Container,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  Chip
} from '@mui/material'
import {
  CheckCircleOutline,
  TrendingUp,
  Security,
  Speed,
  VerifiedUser,
  EmojiObjects
} from '@mui/icons-material'

const AboutUs = () => {
  const values = [
    {
      icon: <VerifiedUser />,
      label: 'Transparência',
      description: 'Informações claras, honestas e completas'
    },
    {
      icon: <EmojiObjects />,
      label: 'Organização',
      description:
        'Cada imóvel no lugar certo, filtros intuitivos e estrutura lógica'
    },
    {
      icon: <CheckCircleOutline />,
      label: 'Qualidade',
      description: 'Fotos boas, descrições relevantes e dados completos'
    },
    {
      icon: <Speed />,
      label: 'Tecnologia eficiente',
      description: 'Rapidez, estabilidade e inteligência na navegação'
    },
    {
      icon: <Security />,
      label: 'Segurança',
      description: 'Validação de informações e proteção de dados'
    },
    {
      icon: <TrendingUp />,
      label: 'Experiência do usuário',
      description: 'Usabilidade simples, direta e fluida'
    }
  ]

  const problems = [
    'Anúncios duplicados',
    'Informações incompletas',
    'Fotos ruins',
    'Filtros confusos',
    'Navegação lenta',
    'Ausência de curadoria real',
    'Experiência fraca para proprietários e corretores'
  ]

  const solutions = [
    'Anúncios mais completos',
    'Filtros mais eficientes',
    'Dados melhor estruturados',
    'Experiência realmente inteligente'
  ]

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2
            }}
          >
            Sobre Nós – umarkt
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            A umarkt é um marketplace imobiliário criado para facilitar a
            conexão entre pessoas que desejam comprar, vender ou anunciar
            imóveis em qualquer região do Brasil.
          </Typography>
        </Box>

        {/* Descrição Principal */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 4, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='body1'
            sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
          >
            Nossa plataforma foi desenvolvida com foco em{' '}
            <strong>simplicidade</strong>, <strong>organização</strong>,{' '}
            <strong>tecnologia</strong> e <strong>transparência</strong>,
            permitindo que usuários encontrem imóveis de forma rápida, intuitiva
            e segura.
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Somos um portal moderno, construído para quem deseja uma experiência
            imobiliária mais clara, eficiente e confiável — algo que ainda falta
            em grande parte dos portais tradicionais.
          </Typography>
        </Paper>

        <Divider sx={{ my: 5 }} />

        {/* Nossa História */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            Nossa História
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Typography
              variant='body1'
              sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}
            >
              A umarkt nasceu da percepção de problemas comuns nos portais
              imobiliários mais usados:
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {problems.map((problem, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'error.main'
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary' }}
                    >
                      {problem}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography
              variant='body1'
              sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}
            >
              Decidimos então construir um marketplace que resolve esses pontos.
              Nosso foco é oferecer um ambiente organizado, rápido e
              profissional, onde o usuário encontra o que procura sem perder
              tempo, e onde cada anúncio ganha a visibilidade que merece.
            </Typography>
            <Typography
              variant='body1'
              sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}
            >
              Queremos elevar o padrão do mercado, trazendo:
            </Typography>
            <Grid container spacing={2}>
              {solutions.map((solution, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutline
                      sx={{ color: 'success.main', fontSize: '1.2rem' }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary' }}
                    >
                      {solution}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Missão e Visão */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 3
              }}
            >
              <Typography variant='h5' sx={{ fontWeight: 700, mb: 2 }}>
                Nossa Missão
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                Facilitar a jornada de quem busca ou anuncia um imóvel,
                entregando uma plataforma moderna, transparente e eficiente,
                onde qualidade e organização são prioridades.
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                Ser reconhecido como um marketplace imobiliário inteligente,
                confiável e simples de usar, oferecendo uma experiência superior
                tanto para quem anuncia quanto para quem procura um imóvel.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        {/* Nossos Valores */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Nossos Valores
          </Typography>
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}
                key={index}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 8px 24px rgba(255, 127, 39, 0.15)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: 'primary.main'
                    }}
                  >
                    {React.cloneElement(value.icon, {
                      sx: { fontSize: '2rem' }
                    })}
                  </Box>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    {value.label}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                  >
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Como Funciona */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            Como Funciona
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Typography
              variant='body1'
              sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}
            >
              O funcionamento da umarkt se baseia em quatro pilares:
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  number: '1',
                  title: 'Curadoria Inteligente',
                  description:
                    'Nosso sistema identifica os anúncios mais relevantes usando critérios como fotos, preço, atratividade e engajamento.'
                },
                {
                  number: '2',
                  title: 'IQA – Índice de Qualidade do Anúncio',
                  description:
                    'Anúncios com informações completas e boa apresentação ganham prioridade natural.'
                },
                {
                  number: '3',
                  title: 'Estrutura organizada por filtros',
                  description:
                    'O usuário encontra rapidamente o que procura, filtrando por tipo, tamanho, faixa de preço, região e características do imóvel.'
                },
                {
                  number: '4',
                  title: 'Ranking inteligente',
                  description:
                    'Nosso ranking prioriza relevância + qualidade + experiência do usuário, garantindo justiça entre quem anuncia e eficácia para quem busca.'
                }
              ].map((item, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Box
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                  >
                    <Chip
                      label={item.number}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1rem',
                        height: 40,
                        width: 40,
                        borderRadius: '50%'
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Nosso Compromisso */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'grey.50',
            borderRadius: 3,
            borderLeft: '4px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            Nosso Compromisso
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
          >
            Nosso objetivo é continuar evoluindo o marketplace, mantendo a
            experiência do usuário como prioridade máxima. Trabalhamos para
            oferecer um ambiente confiável, organizado e eficiente para:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              'Compradores',
              'Proprietários',
              'Locadores',
              'Corretores',
              'Imobiliárias'
            ].map((type, index) => (
              <Chip
                key={index}
                label={type}
                sx={{
                  bgcolor: 'white',
                  fontWeight: 500,
                  borderColor: 'grey.300'
                }}
                variant='outlined'
              />
            ))}
          </Box>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Queremos que cada pessoa que usa o portal sinta que está navegando
            em um serviço moderno, claro e bem construído.
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

export default AboutUs
